import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../../stores/authStore';
import { getFirstAllowedRoute } from '../../../lib/nav-routes';
import { LoginBackground } from '../../../components/auth/LoginBackground';
import { withAreaPrefix } from '../../../lib/area-routes';

interface LoginFormData {
  email: string;
  password: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('auth');
  const { login, isLoading, hasScope } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  // Helper function to extract translation key
  const getTranslationKey = (messageKey: string): string => {
    return messageKey.startsWith('auth.') ? messageKey.replace('auth.', '') : messageKey;
  };

  // Helper function to clear all errors
  const clearAllErrors = () => {
    setError(null);
    setEmailError(null);
    setPasswordError(null);
  };

  // Helper function to handle validation error details
  const handleValidationDetails = (details: unknown[], translationKey: string) => {
    if (Array.isArray(details) && details.length > 0) {
      details.forEach((detail: unknown) => {
        const d = detail as { field?: string; message?: string };
        const field = d.field?.toLowerCase();
        const detailMessage = d.message || t(translationKey || 'errors.validation_error');

        if (field === 'email') {
          setEmailError(detailMessage);
        } else if (field === 'password') {
          setPasswordError(detailMessage);
        }
      });
    } else {
      // No details, show general validation error
      setError(t(translationKey || 'errors.validation_error'));
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      // Clear all errors on submit
      clearAllErrors();

      await login(data.email, data.password);

      // Clear errors on successful login
      clearAllErrors();

      toast.success(t('success.login'));
      const currentUser = useAuthStore.getState().user;
      const preferred = getFirstAllowedRoute(
        hasScope,
        location.pathname,
        currentUser?.isPlatformUser
      );
      navigate(preferred);
    } catch (err: unknown) {
      const e = err as {
        response?: { data?: { code?: string; messageKey?: string; details?: unknown[] } };
        code?: string;
      };
      // Handle network errors (no response, timeout, etc.)
      if (!e.response) {
        toast.error(t('errors.network_error'));
        return; // Toast only, no inline errors
      }

      // Extract error code and messageKey from error response
      const errorCode = e.response?.data?.code || e.code || 'UNKNOWN_ERROR';
      const messageKey = e.response?.data?.messageKey || 'errors.generic';
      const details = e.response?.data?.details; // For VALIDATION_ERROR

      // Extract translation key (remove 'auth.' prefix if present since t is scoped to 'auth' namespace)
      const translationKey = getTranslationKey(messageKey);

      // Handle different error codes based on design doc mapping
      switch (errorCode) {
        case 'EMAIL_NOT_VERIFIED': {
          // Email field error only
          const errorMsg = t(translationKey || 'errors.email_not_verified');
          setEmailError(errorMsg);
          toast.error(errorMsg);
          break;
        }

        case 'INVALID_CREDENTIALS': {
          // Both email and password fields
          const invalidCredsMessage = t(translationKey || 'errors.invalid_credentials');
          setEmailError(invalidCredsMessage);
          setPasswordError(invalidCredsMessage);
          toast.error(invalidCredsMessage);
          break;
        }

        case 'USER_DISABLED': {
          // Banner error (not inline)
          const errorMsg = t(translationKey || 'errors.user_disabled');
          setError(errorMsg);
          toast.error(errorMsg);
          break;
        }

        case 'RATE_LIMIT_EXCEEDED': {
          // Banner error (not inline)
          const errorMsg = t(translationKey || 'errors.rate_limit_exceeded');
          setError(errorMsg);
          toast.error(errorMsg);
          break;
        }

        case 'NETWORK_ERROR':
          // Toast only (no inline errors)
          toast.error(t(translationKey || 'errors.network_error'));
          break;

        case 'SERVICE_UNAVAILABLE':
          // Toast only (no inline errors)
          toast.error(t(translationKey || 'errors.service_unavailable'));
          break;

        case 'VALIDATION_ERROR': {
          // Handle details array if present
          handleValidationDetails(details ?? [], translationKey);
          toast.error(t(translationKey || 'errors.validation_error'));
          break;
        }

        default: {
          // Unknown errors - fallback to generic messageKey
          const fallbackMessage = t(translationKey || 'errors.generic');
          setError(fallbackMessage);
          toast.error(fallbackMessage);
          break;
        }
      }
    }
  };

  return (
    <LoginBackground>
      <div className="max-w-md w-full space-y-6 sm:space-y-8 p-6 sm:p-8 bg-violet-500/20 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20">
        <div className="space-y-3 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
            {t('login.title')}
          </h2>
          <p className="text-sm text-white/80">{t('login.subtitle')}</p>
        </div>
        <form className="mt-4 sm:mt-6 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <div className="bg-red-500/30 border border-red-400/50 text-red-100 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/90">
                {t('login.email_label')}
              </label>
              <input
                {...register('email', {
                  required: t('validation.email_required'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('validation.invalid_email'),
                  },
                  onChange: () => {
                    setEmailError(null);
                  },
                })}
                type="email"
                id="email"
                data-testid="login-email"
                autoComplete="email"
                aria-invalid={!!errors.email || !!emailError}
                className="mt-1 block w-full px-3 py-2.5 border border-white/30 rounded-xl bg-white/15 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40"
                placeholder="you@example.com"
              />
              {errors.email && <p className="mt-1 text-sm text-red-200">{errors.email.message}</p>}
              {emailError && <p className="mt-1 text-sm text-red-200">{emailError}</p>}
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/90">
                {t('login.password_label')}
              </label>
              <input
                {...register('password', {
                  required: t('validation.password_required'),
                  minLength: {
                    value: 8,
                    message: t('validation.password_min_length'),
                  },
                  onChange: () => {
                    setPasswordError(null);
                  },
                })}
                type="password"
                id="password"
                data-testid="login-password"
                autoComplete="current-password"
                aria-invalid={!!errors.password || !!passwordError}
                className="mt-1 block w-full px-3 py-2.5 border border-white/30 rounded-xl bg-white/15 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40"
                placeholder="••••••••"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-200">{errors.password.message}</p>
              )}
              {passwordError && <p className="mt-1 text-sm text-red-200">{passwordError}</p>}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link
                to={withAreaPrefix('/auth/forgot-password', location.pathname)}
                className="font-medium text-white/90 hover:text-white transition-colors duration-200"
              >
                {t('login.forgot_password_link')}
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              data-testid="login-submit"
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-transparent rounded-xl text-sm font-semibold text-violet-900 bg-white hover:bg-white/95 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer shadow-lg"
            >
              {isLoading ? t('login.submit_button_loading') : t('login.submit_button')}
            </button>
          </div>

          <div className="text-center text-sm">
            <span className="text-white/80">{t('login.register_link')} </span>
            <Link
              to={withAreaPrefix('/auth/register', location.pathname)}
              className="font-medium text-white hover:text-white/90 transition-colors duration-200 underline underline-offset-2"
            >
              {t('login.register_link_text')}
            </Link>
          </div>
        </form>
      </div>
    </LoginBackground>
  );
}
